
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { CompanyData } from '@/types';
import { getRedirectUrl, generateSubmissionIdentifier, normalizeAlternativeEmails } from './helpers';
import { pickOrganisationForMagicLink } from './resolveMagicLinkOrganisation';
import { readFunctionErrorBody } from '@/utils/functionErrorBody';

export type LinkType = 'onboarding' | 'reseller_portal';

export const sendMagicLinkEmail = async (
  email: string,
  alternativeEmails?: string | string[],
  companyData?: CompanyData,
  isDraft: boolean = false,
  linkType: LinkType = 'onboarding',
  expiresAt?: string
): Promise<{ success: boolean; error?: string; submissionIdentifier?: string }> => {
  try {
    console.log('[sendMagicLinkEmail] Starting with params:', {
      email,
      alternativeEmails,
      companyData,
      isDraft,
      linkType
    });

    // Generate a unique submission identifier for this specific magic link request
    // This avoids the duplicate key constraint error by ensuring each request gets a unique identifier
    const submissionIdentifier = generateSubmissionIdentifier();

    // Use the provided organizationId, or try to get it from companyData
    let finalOrganisationId = companyData?.organization_id;

    console.log('[sendMagicLinkEmail] Initial organisation_id:', finalOrganisationId);

    if (!finalOrganisationId && !isDraft) {
      console.log('[sendMagicLinkEmail] No org ID provided, attempting fallback query for email:', email);

      // maybeSingle errors when a test clone shares this email, which looks
      // like "no company". Load the matches and keep the non-clone org.
      const { data: existingOrgs, error: lookupError } = await supabase
        .from('organisations')
        .select('id, is_test_clone')
        .eq('poc_email', email)
        .order('is_test_clone', { ascending: true });

      if (lookupError) {
        console.error('[sendMagicLinkEmail] Fallback query error:', lookupError);
      }

      const existingOrg = pickOrganisationForMagicLink(existingOrgs ?? [], { allowTestClone: true });
      if (existingOrg) {
        finalOrganisationId = existingOrg.id;
        console.log('[sendMagicLinkEmail] Found org via fallback:', finalOrganisationId);
      } else {
        console.warn('[sendMagicLinkEmail] No organization found via fallback for email:', email);
      }
    }

    // CRITICAL: Do not proceed if we don't have an organisation_id for non-draft sessions
    if (!finalOrganisationId && !isDraft) {
      const errorMsg = 'Cannot create magic link session: organization_id is required for non-draft sessions';
      console.error('[sendMagicLinkEmail]', errorMsg);
      return {
        success: false,
        error: errorMsg
      };
    }

    // Get proper redirect URL based on environment and link type
    let redirectTo = getRedirectUrl();

    // For reseller portal links, use a different callback path
    if (linkType === 'reseller_portal') {
      redirectTo = redirectTo.replace('/auth-callback', '/reseller/auth-callback');
    }

    // Ensure we have a valid array of alternative emails
    const validAlternativeEmails = normalizeAlternativeEmails(alternativeEmails);

    console.log('[sendMagicLinkEmail] Creating session with:', {
      email,
      submission_identifier: submissionIdentifier,
      organisation_id: finalOrganisationId,
      is_draft: isDraft,
      link_type: linkType,
      redirectTo,
      alternative_email: validAlternativeEmails
    });

    // Determine which email to send the magic link to
    const destinationEmail = validAlternativeEmails.length > 0 ? validAlternativeEmails[0] : email;

    // For reseller portal links, use the custom edge function with Resend
    if (linkType === 'reseller_portal') {
      console.log('[sendMagicLinkEmail] Using custom reseller magic link flow');
      
      const { data: resellerResponse, error: resellerError } = await invokeFunction(supabase, 'send-reseller-magic-link-v3', {
        body: {
          email,
          userName: companyData?.pocName || 'there',
          organisationId: finalOrganisationId,
          submissionIdentifier,
          alternativeEmail: validAlternativeEmails.length > 0 ? validAlternativeEmails[0] : undefined,
          expiresAt
        }
      });

      if (resellerError || !resellerResponse?.success) {
        const errorMsg = resellerError?.message || resellerResponse?.error || 'Failed to send reseller magic link';
        console.error('[sendMagicLinkEmail] Reseller magic link failed:', errorMsg);
        throw new Error(errorMsg);
      }

      console.log('[sendMagicLinkEmail] Reseller magic link sent successfully');
      return {
        success: true,
        submissionIdentifier
      };
    }

    // For onboarding links, use the existing signInWithOtp flow
    // Store magic link session with phone number information and link type
    const sessionData = {
      email,
      submission_identifier: submissionIdentifier,
      organisation_id: finalOrganisationId || null,
      is_draft: isDraft,
      poc_phone: companyData?.pocPhone || null,
      alternative_email: validAlternativeEmails.length > 0 ? validAlternativeEmails : null,
      link_type: linkType
    };

    // Insert new session via edge function
    const { data: sessionResponse, error: sessionError } = await invokeFunction(supabase, 'update-magic-link-session', {
      body: {
        action: 'insert',
        sessionData
      }
    });

    if (sessionError || !sessionResponse?.success) {
      const errorMsg = sessionError?.message || sessionResponse?.error || 'Failed to insert session';
      console.error('[sendMagicLinkEmail] Session creation failed:', errorMsg);
      throw new Error(errorMsg);
    }

    console.log('[sendMagicLinkEmail] Session created successfully');

    const recipientParam = destinationEmail !== email ? `&recipient_email=${encodeURIComponent(destinationEmail)}` : '';
    const emailRedirectTo = `${redirectTo}?submission_identifier=${submissionIdentifier}${recipientParam}`;
    console.log('[sendMagicLinkEmail] Sending OTP to:', destinationEmail, { emailRedirectTo });

    const { data: otpData, error } = await supabase.auth.signInWithOtp({
      email: destinationEmail,
      options: {
        emailRedirectTo,
      }
    });
    console.log({ otpData })

    if (error) {
      console.error('[sendMagicLinkEmail] OTP send failed:', error);
      throw error;
    }

    console.log('[sendMagicLinkEmail] Magic link sent successfully to:', destinationEmail);

    return {
      success: true,
      submissionIdentifier
    };

  } catch (error) {
    console.error('[sendMagicLinkEmail] Fatal error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send magic link email'
    };
  }
};

// The caller holds no session here (the link failed), so the edge function
// resolves the recipient and organisation from the original submission.
// resendProof is the link's resend_proof parameter, signed when it was sent.
export const requestResellerMagicLinkResend = async (
  previousSubmissionIdentifier: string,
  resendProof: string
): Promise<{ success: boolean; error?: string; submissionIdentifier?: string }> => {
  if (!previousSubmissionIdentifier) {
    return { success: false, error: 'Could not find the original portal link' };
  }

  try {
    const { data } = await invokeFunction<{ success?: boolean; error?: string; submissionIdentifier?: string }>(
      supabase,
      'send-reseller-magic-link-v3',
      { body: { resendSubmissionIdentifier: previousSubmissionIdentifier, resendProof } }
    );
    if (!data?.success) {
      return { success: false, error: data?.error || 'Failed to send magic link' };
    }
    return { success: true, submissionIdentifier: data.submissionIdentifier };
  } catch (error) {
    const body = await readFunctionErrorBody<{ error?: string }>(error);
    return {
      success: false,
      error: body?.error || (error instanceof Error ? error.message : 'Failed to send magic link'),
    };
  }
};
